<svelte:options runes={false} />
<script>
  // The decision inspector (TASK-20260728-researcher-observability-ui): the "why" panel. Renders one
  // decision's causality chain — regime gate → signal vs threshold → fired rule + holding stats → sizing →
  // execution outcome — as named fields (describeDecision), with the verbatim signal_snapshot behind a
  // <details> disclosure for full auditability. Text interpolation only (XSS-safe, mirrors the error banner).
  import { createEventDispatcher } from 'svelte';
  import { describeDecision, decisionMeta, exitRuleMeta } from '../../lib/lineage.js';

  export let decision = null; // a DecisionRow, or null (empty state)

  const dispatch = createEventDispatcher();

  $: meta = decision ? decisionMeta(decision.decisionType) : null;
  $: rule = decision && (decision.decisionType === 'exit' || decision.decisionType === 'external_close')
    ? exitRuleMeta(decision.exitRule)
    : null;
  $: sections = decision ? describeDecision(decision) : [];
  $: snapshotJson = decision ? JSON.stringify(decision.signalSnapshot ?? {}, null, 2) : '';

  function fmtTime(t) {
    const d = new Date(t);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
  }
</script>

<aside class="di-panel" aria-label="Decision inspector">
  {#if !decision}
    <div class="di-empty">
      <p>Select a decision on the tape or in the blotter to see its full causality chain — regime, scores, fired rule, sizing and execution.</p>
    </div>
  {:else}
    <header class="di-header">
      <div class="di-title">
        <span class="di-glyph" style={`color:${decision.decisionType === 'exit' && rule ? rule.color : meta.color}`}>{meta.glyph}</span>
        <span class="di-kind">{meta.label}</span>
        {#if decision.symbol}
          <span class="di-symbol">{decision.symbol.replace('_', '-')}</span>
        {/if}
        <button type="button" class="di-close" aria-label="Close inspector" on:click={() => dispatch('close')}>×</button>
      </div>
      {#if rule && rule.id !== null}
        <div class="di-rule" style={`border-color:${rule.color}`}>
          <span class="di-rule-id" style={`background:${rule.color}`}>EXIT {rule.id}</span>
          <span class="di-rule-label">{rule.label}</span>
          {#if rule.detail}<span class="di-rule-detail">{rule.detail}</span>{/if}
        </div>
      {/if}
      {#if decision.decisionType === 'external_close'}
        <p class="di-note">Closed by the framework (fired protective stop or divergence reconcile), not by the strategy's exit engine.</p>
      {/if}
      <dl class="di-times">
        <div><dt>Bar</dt><dd>{fmtTime(decision.barTime)}</dd></div>
        <div><dt>Decided</dt><dd>{fmtTime(decision.decidedAt)}</dd></div>
        {#if decision.orgId}<div><dt>Org</dt><dd>{decision.orgId}</dd></div>{/if}
      </dl>
    </header>

    <div class="di-chain">
      {#each sections as section, i (section.title)}
        <section class="di-section">
          <h5 class="di-section-title"><span class="di-step">{i + 1}</span>{section.title}</h5>
          {#if section.note}
            <p class="di-note">{section.note}</p>
          {/if}
          {#if section.fields.length > 0}
            <dl class="di-fields">
              {#each section.fields as f (f.label)}
                <div class="di-field">
                  <dt>{f.label}</dt>
                  <dd class="num">
                    {#if f.ruleId !== undefined}
                      <span class="di-rule-chip" style={`border-color:${exitRuleMeta(f.ruleId).color}`}>{f.value}</span>
                    {:else}
                      {f.value}
                    {/if}
                  </dd>
                </div>
              {/each}
            </dl>
          {/if}
        </section>
      {/each}
    </div>

    <details class="di-raw">
      <summary>Verbatim signal snapshot (audit record)</summary>
      <pre>{snapshotJson}</pre>
    </details>
  {/if}
</aside>

<style>
  .di-panel {
    border: 1px solid var(--border-subtle, #1e1f22);
    border-radius: 8px;
    background: var(--surface-1, rgba(255, 255, 255, 0.02));
    padding: 0.85rem;
    font-size: 0.8rem;
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    min-height: 200px;
  }
  .di-empty { color: var(--text-muted, #8a8b8d); font-size: 0.8rem; }
  .di-header { display: flex; flex-direction: column; gap: 0.45rem; }
  .di-title { display: flex; align-items: center; gap: 0.45rem; }
  .di-glyph { font-size: 1rem; line-height: 1; }
  .di-kind { font-weight: 700; color: var(--text-primary, #e8e9ea); }
  .di-symbol { color: var(--text-secondary, #b5b6b8); font-variant-numeric: tabular-nums; }
  .di-close {
    margin-left: auto; appearance: none; background: transparent; border: none;
    color: var(--text-muted, #8a8b8d); font-size: 1rem; cursor: pointer; line-height: 1; padding: 0.1rem 0.3rem;
  }
  .di-close:hover { color: var(--text-primary, #e8e9ea); }
  .di-rule {
    display: flex; align-items: baseline; gap: 0.45rem; flex-wrap: wrap;
    border-left: 3px solid; padding: 0.3rem 0.5rem;
    background: var(--surface-2, rgba(255, 255, 255, 0.03)); border-radius: 0 4px 4px 0;
  }
  .di-rule-id { font-size: 0.65rem; font-weight: 700; color: #0a0b0d; padding: 0.1rem 0.35rem; border-radius: 3px; }
  .di-rule-label { font-weight: 700; color: var(--text-primary, #e8e9ea); }
  .di-rule-detail { color: var(--text-muted, #8a8b8d); font-size: 0.72rem; }
  .di-times { display: flex; gap: 1rem; margin: 0; flex-wrap: wrap; }
  .di-times div { display: flex; flex-direction: column; }
  .di-times dt { font-size: 0.62rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-muted, #8a8b8d); }
  .di-times dd { margin: 0; color: var(--text-secondary, #b5b6b8); font-variant-numeric: tabular-nums; }
  .di-chain { display: flex; flex-direction: column; gap: 0.55rem; }
  .di-section { border-top: 1px solid var(--border-subtle, #1e1f22); padding-top: 0.5rem; }
  .di-section-title {
    margin: 0 0 0.35rem; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.05em;
    color: var(--text-secondary, #b5b6b8); display: flex; align-items: center; gap: 0.4rem;
  }
  .di-step {
    display: inline-flex; align-items: center; justify-content: center;
    width: 1.05rem; height: 1.05rem; border-radius: 50%;
    border: 1px solid var(--border-subtle, #2a2c31); color: var(--text-muted, #8a8b8d);
    font-size: 0.62rem; font-weight: 700;
  }
  .di-fields { display: grid; grid-template-columns: 1fr auto; gap: 0.18rem 0.9rem; margin: 0; }
  .di-field { display: contents; }
  .di-field dt { color: var(--text-muted, #8a8b8d); }
  .di-field dd { margin: 0; text-align: right; color: var(--text-primary, #e8e9ea); }
  .di-field dd.num { font-variant-numeric: tabular-nums; }
  .di-rule-chip { border: 1px solid; border-radius: 4px; padding: 0.05rem 0.35rem; font-size: 0.72rem; }
  .di-note { margin: 0; color: var(--text-muted, #8a8b8d); font-size: 0.75rem; }
  .di-raw { border-top: 1px solid var(--border-subtle, #1e1f22); padding-top: 0.4rem; }
  .di-raw summary { cursor: pointer; font-size: 0.72rem; color: var(--text-muted, #8a8b8d); }
  .di-raw pre {
    margin: 0.4rem 0 0; padding: 0.5rem; max-height: 260px; overflow: auto;
    background: var(--surface-2, #101114); border-radius: 4px;
    font-size: 0.7rem; color: var(--text-secondary, #b5b6b8);
  }
</style>
